import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Confirms a booking **on Zocdoc** (`POST /v1/appointments/confirm`).
 *
 * Its own route rather than a branch of an update endpoint, for the reason set out in
 * `zocdoc-cancel-appointment.contract.ts`: Zocdoc's four appointment writes take disjoint payloads
 * and are legal from different statuses. This one takes nothing but the id and is legal from
 * `pending_booking` alone.
 *
 * **Writes nothing to FHIR.** A confirmation changes no beHuman resource — the booking is either
 * already imported or it is not, and neither is affected by Zocdoc's own state moving from
 * pending to confirmed.
 */
export const zocdocConfirmAppointment = oc
  .route({ method: 'POST', path: '/v2/zocdoc/appointments/confirm' })
  .input(z.object({ zocdocAppointmentId: z.string().min(1) }))
  .output(
    z.object({
      status: z.enum([
        /** Zocdoc accepted the confirmation. */
        'confirmed',
        /** Zocdoc already had it confirmed — nothing was sent. */
        'already-confirmed',
        /** Nothing was sent. See `refusal`. */
        'refused',
      ]),
      refusal: z
        .object({
          reason: z.enum([
            'not-found',
            /** Only `pending_booking` can be confirmed; the detail names what it actually is. */
            'status-not-confirmable',
            'zocdoc-rejected',
          ]),
          detail: z.string(),
        })
        .optional(),
      previousStatus: z.string().optional(),
      /**
       * What Zocdoc reported when the appointment was re-read **immediately after** the write.
       *
       * Exists because a successful write plus an unchanged row is otherwise silent: the screen
       * re-reads the list, shows the same status, and cannot say whether Zocdoc rejected the
       * change or has simply not caught up. Absent when the verifying read failed — which is not
       * itself a failure of the write.
       */
      observedStatus: z.string().optional(),
    }),
  )
